import React from 'react';
import PropTypes from 'prop-types';
import { Card, HeaderCard, TitleCard, ContentCard, Form, SelectLabel, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { InternalResaleSettingsModel } from '../../../../models';
import ToggleCardTemplate from './toggleCardTemplate';
import Styles from '../resaleSettings.module.css';
import { DEFAULT_IDENTITY } from '../utils';

function ResaleNotifications({ form, updateForm, organization, event, templates, loadingTemplates, loading }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [identities, setIdentities] = React.useState([]);
    const [loadingIdentities, setLoadingIdentities] = React.useState(true);

    React.useEffect(() => {
        InternalResaleSettingsModel.getIdentities({ organizationId: organization.id, eventId: event.id })
            .then(data => {
                setIdentities(data.response.identities || []);
            })
            .catch(() => {
                toast.error(t(`ticket.resales.settings.notifications.identities.error`));
            })
            .finally(() => setLoadingIdentities(false));
    }, [organization.id, event.id]);

    const identityOptions = React.useMemo(() => {
        const options = identities.map(identity => ({
            key: identity.id,
            text: (
                <div className={Styles['identity-option']}>
                    <div>{`${identity.from_address.name} <${identity.from_address.email}>`}</div>
                    <div className={Styles['identity-option-reply-to']}>
                        {t('ticket.resales.settings.notifications.reply_to')} {identity.reply_to.email}
                    </div>
                </div>
            ),
            value: identity.id
        }));

        // Add default identity option at the top of the list
        options.unshift({
            key: 'default',
            text: (
                <div className={Styles['identity-option']}>
                    <div>{`${DEFAULT_IDENTITY.from_address.name} <${DEFAULT_IDENTITY.from_address.email}>`}</div>
                    <div className={Styles['identity-option-reply-to']}>
                        {t('ticket.resales.settings.notifications.reply_to')} {DEFAULT_IDENTITY.reply_to.email}
                    </div>
                </div>
            ),
            value: null
        });

        return options;
    }, [identities]);

    return (
        <Card>
            <HeaderCard>
                <TitleCard>{t('ticket.resales.settings.notifications.title')}</TitleCard>
            </HeaderCard>
            <ContentCard>
                <Form>
                    <Form.Field>
                        <Form.Label label={t('ticket.resales.settings.notifications.email_sender')} />
                        <SelectLabel
                            className={Styles['select-identity']}
                            placeholder={t('ticket.resales.settings.notifications.email_sender_placeholder')}
                            options={identityOptions}
                            value={form.identity_id}
                            onChange={value => updateForm('identity_id', value)}
                            loading={loading || loadingIdentities}
                            disabled={loading || loadingIdentities}
                            clearable
                        />
                    </Form.Field>
                    <ToggleCardTemplate
                        toggleTitle={t('ticket.resales.settings.notifications.ticket_on_sale_label')}
                        toggleSubtitle={t('ticket.resales.settings.notifications.ticket_on_sale_description')}
                        toggleValue={form.notify_ticket_on_resale}
                        onToggleChange={checked => updateForm('notify_ticket_on_resale', checked)}
                        templateValue={form.template_id_ticket_on_resale}
                        onTemplateChange={value => updateForm('template_id_ticket_on_resale', value)}
                        templates={templates.onResale || []}
                        loading={loading || loadingTemplates}
                    />

                    <ToggleCardTemplate
                        toggleTitle={t('ticket.resales.settings.notifications.ticket_sold_label')}
                        toggleSubtitle={t('ticket.resales.settings.notifications.ticket_sold_description')}
                        toggleValue={form.notify_ticket_resold}
                        onToggleChange={checked => updateForm('notify_ticket_resold', checked)}
                        templateValue={form.template_id_ticket_resold}
                        onTemplateChange={value => updateForm('template_id_ticket_resold', value)}
                        templates={templates.resold || []}
                        loading={loading || loadingTemplates}
                    />
                </Form>
            </ContentCard>
        </Card>
    );
}

ResaleNotifications.propTypes = {
    form: PropTypes.object.isRequired,
    updateForm: PropTypes.func.isRequired,
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    loading: PropTypes.bool
};

export default ResaleNotifications;
